import React, { useState, useEffect, useCallback } from 'react';
import BacktestSummary from './BacktestSummary';

// Refresh Icon
const RefreshIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);
import EquityCurveChart from './charts/EquityCurveChart';
import DrawdownChart from './charts/DrawdownChart';
import DailyReturnsChart from './charts/DailyReturnsChart';
import MonthlyHeatmap from './charts/MonthlyHeatmap';
import WinLossChart from './charts/WinLossChart';
import ReturnsHistogram from './charts/ReturnsHistogram';
import TradePnLHistogram from './charts/TradePnLHistogram';
import TradeDurationChart from './charts/TradeDurationChart';
import PriceWithSignalsChart from './charts/PriceWithSignalsChart';
import TradesTable from './charts/TradesTable';
import { backtestService } from '../../config/api';
import { transformBacktestResult, transformSeriesData } from '../../utils/backtestDataTransformer';

const ChartCard = ({ children, fullWidth = false }) => (
  <div className={`${fullWidth ? 'col-span-2' : ''}`}>
    <div className="card bg-base-100 shadow hover:shadow-lg transition-shadow p-4 h-full">
      {children}
    </div>
  </div>
);

/**
 * BacktestDashboard Component
 * 
 * Displays backtest results with charts and metrics.
 * Can receive data directly via `data` prop or fetch from API via `backtestId` prop.
 * 
 * @param {Object} props
 * @param {Object} props.data - Direct backtest data (optional, takes precedence)
 * @param {string} props.backtestId - ID to fetch backtest from API (optional)
 * @param {Function} props.onError - Error callback (optional)
 */
const BacktestDashboard = ({ data: propData, backtestId, onError }) => {
  const [data, setData] = useState(null);
  const [seriesData, setSeriesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch backtest metadata from API
  const fetchBacktestData = useCallback(async () => {
    if (!backtestId) return;

    setLoading(true);
    setSeriesLoading(true); // Start showing spinners for charts immediately
    setError(null);

    try {
      const response = await backtestService.getById(backtestId);
      const transformedData = transformBacktestResult(response.data);
      setData(transformedData);
    } catch (err) {
      console.error('Error fetching backtest:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load backtest data';
      setError(errorMessage);
      setSeriesLoading(false); // Stop series loading on error
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [backtestId, onError]);

  // Fetch series data separately (lazy loading for charts)
  const fetchSeriesData = useCallback(async (id) => {
    if (!id) return;

    setSeriesLoading(true);
    try {
      const response = await backtestService.getSeriesData(id);
      setSeriesData(transformSeriesData(response.data));
    } catch (err) {
      console.error('Error fetching series data:', err);
      // Fallback to embedded data if available (for backward compatibility)
    } finally {
      setSeriesLoading(false);
    }
  }, []);

  // Effect: Use prop data or fetch from API
  useEffect(() => {
    if (propData) {
      // If data is passed directly, transform it if needed
      const transformedData = propData.symbol ? propData : transformBacktestResult(propData);
      setData(transformedData);
      setError(null);
      // For prop data, series might be included or need separate fetch
      if (transformedData.equitySeries) {
        setSeriesData({
          equitySeries: transformedData.equitySeries,
          returnsSeries: transformedData.returnsSeries,
          priceSeries: transformedData.priceSeries,
          exposureSeries: transformedData.exposureSeries,
        });
      } else if (transformedData.id) {
        fetchSeriesData(transformedData.id);
      }
    } else if (backtestId) {
      fetchBacktestData();
    }
  }, [propData, backtestId, fetchBacktestData, fetchSeriesData]);

  // Effect: Fetch series data after backtest metadata is loaded
  useEffect(() => {
    if (data?.id && !propData && !seriesData) {
      fetchSeriesData(data.id);
    }
  }, [data, propData, seriesData, fetchSeriesData]);

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <span className="loading loading-spinner loading-lg"></span>
        <span className="ml-4">Loading backtest results...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <div className="alert alert-error">
          <span>{error}</span>
          {backtestId && (
            <button className="btn btn-ghost btn-sm gap-1" onClick={fetchBacktestData}>
              <RefreshIcon /> Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // No data state
  if (!data) {
    return (
      <div className="p-6">
        <div className="alert alert-info">
          <span>No backtest data available. Run a backtest to see results.</span>
        </div>
      </div>
    );
  }

  const {
    strategyName,
    symbol,
    timeframe,
    trades,
    trendLines,
    summary,
    chargesSummary
  } = data;

  // Use series data from separate fetch or fallback to embedded data
  const equitySeries = seriesData?.equitySeries || data.equitySeries;
  const returnsSeries = seriesData?.returnsSeries || data.returnsSeries;
  const priceSeries = seriesData?.priceSeries || data.priceSeries;

  return (
    <div>
      {/* Summary Metrics */}
      <BacktestSummary
        summary={summary}
        strategyName={strategyName}
        symbol={symbol}
        timeframe={timeframe}
        tradingType={data.tradingType}
        startDate={data.startDate}
        endDate={data.endDate}
        chargesSummary={chargesSummary}
      />

      <div className="divider my-6"></div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Trades Table - Full Width */}
        <ChartCard fullWidth>
          <TradesTable trades={trades} maxHeight={300} />
        </ChartCard>

        {/* Equity Curve - Full Width */}
        <ChartCard fullWidth>
          {seriesLoading && (!equitySeries || equitySeries.length === 0) ? (
            <div className="flex flex-col justify-center items-center h-[300px]">
              <span className="loading loading-spinner loading-lg text-info"></span>
              <span className="mt-3 text-base-content/60">Loading equity curve...</span>
            </div>
          ) : (
            <EquityCurveChart equitySeries={equitySeries} height={300} />
          )}
        </ChartCard>

        {/* Drawdown Chart - Full Width */}
        <ChartCard fullWidth>
          {seriesLoading && (!equitySeries || equitySeries.length === 0) ? (
            <div className="flex flex-col justify-center items-center h-[200px]">
              <span className="loading loading-spinner loading-md text-primary"></span>
              <span className="mt-2 text-sm text-base-content/60">Loading drawdown...</span>
            </div>
          ) : (
            <DrawdownChart equitySeries={equitySeries} height={200} />
          )}
        </ChartCard>

        {/* Daily Returns & Monthly Heatmap */}
        <ChartCard>
          {seriesLoading && (!returnsSeries || returnsSeries.length === 0) ? (
            <div className="flex flex-col justify-center items-center h-[250px]">
              <span className="loading loading-spinner loading-md text-primary"></span>
              <span className="mt-2 text-sm text-base-content/60">Loading returns...</span>
            </div>
          ) : (
            <DailyReturnsChart returnsSeries={returnsSeries} height={250} />
          )}
        </ChartCard>
        <ChartCard>
          {seriesLoading && (!returnsSeries || returnsSeries.length === 0) ? (
            <div className="flex flex-col justify-center items-center h-[250px]">
              <span className="loading loading-spinner loading-md text-primary"></span>
              <span className="mt-2 text-sm text-base-content/60">Loading heatmap...</span>
            </div>
          ) : (
            <MonthlyHeatmap returnsSeries={returnsSeries} height={250} />
          )}
        </ChartCard>

        {/* Win/Loss & Returns Histogram */}
        <ChartCard>
          <WinLossChart trades={trades} height={280} />
        </ChartCard>
        <ChartCard>
          {seriesLoading && (!returnsSeries || returnsSeries.length === 0) ? (
            <div className="flex flex-col justify-center items-center h-[280px]">
              <span className="loading loading-spinner loading-md text-primary"></span>
              <span className="mt-2 text-sm text-base-content/60">Loading histogram...</span>
            </div>
          ) : (
            <ReturnsHistogram returnsSeries={returnsSeries} height={280} />
          )}
        </ChartCard>

        {/* Trade PnL & Duration */}
        <ChartCard>
          <TradePnLHistogram trades={trades} height={280} />
        </ChartCard>
        <ChartCard>
          <TradeDurationChart trades={trades} height={280} />
        </ChartCard>

        {/* Price Chart with Signals - Full Width */}
        <ChartCard fullWidth>
          {seriesLoading && (!priceSeries || priceSeries.length === 0) ? (
            <div className="flex flex-col justify-center items-center h-[450px]">
              <span className="loading loading-spinner loading-lg text-primary"></span>
              <span className="mt-3 text-base-content/60">Loading price chart...</span>
            </div>
          ) : (
            <PriceWithSignalsChart
              priceSeries={priceSeries}
              trades={trades}
              trendLines={trendLines}
              height={450}
            />
          )}
        </ChartCard>
      </div>
    </div>
  );
};

export default BacktestDashboard;
